import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import {
    useGetConnectionSequenceStepLogsExportMutation,
    useGetConnectionSequenceStepLogsQuery,
} from "api/TenantSlug/ConnectionSequenceStepLogs/hooks";
import { LogConnectionDrawerDetail } from "components/Drawers/Logs/LogConnectionDrawerDetail";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { ConnectionSequenceStepLogModelType, ConnectionSequenceStepLogResult, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import GetConnectionSequenceStepLogsResponse = App.Http.Responses.ConnectionSequenceStepLog.GetConnectionSequenceStepLogsResponse;
import ConnectionSequenceStepLogData = App.Data.ConnectionSequenceStepLogData;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

export const OutgoingPostbackStepLogsTable = () => {
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const tableId = TableId.TrackingOutLogsTable;
    const tableAtomInstance = tableAtom<ConnectionSequenceStepLogData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const createdAtStr = useCreatedAtFilterValue();
    const exportState = useRecoilValue(exportAtom(tableId));

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                model_type: ConnectionSequenceStepLogModelType.OutgoingPostback,
                ...(tableState.filter?.filter || {}),
                ...(tableState.filter?.filter?.created_at
                    ? {}
                    : {
                          created_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState?.filter?.sort ?? "-created_at",
            include: "connectionSequenceStep,offer,conversionState,owner,advertiser,cascade,outgoingPostback",
        },
    };

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<ConnectionSequenceStepLogData>({
        entityName: "connectionSequenceStepLog",
        detailPermission: PermissionEnum.LogShowDetail,
        detailDrawerId: DrawerId.ConnectionSequenceStepLog,
        detailDrawerParam: DrawerId.ConnectionSequenceStepLog,
    });

    const getConnectionSequenceStepLogsQuery = useGetConnectionSequenceStepLogsQuery(request, { enabled: false });

    const getConnectionSequenceStepLogsExportMutation = useGetConnectionSequenceStepLogsExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetConnectionSequenceStepLogsResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, getConnectionSequenceStepLogsQuery, undefined, undefined, getConnectionSequenceStepLogsExportMutation);

    const permissionsUtils = usePermission();

    const columnConfig: TableColumnType<ConnectionSequenceStepLogData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "created_at",
            key: "created_at",
            inputType: "text",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            key: "conversionState.origin_eid",
            dataIndex: "conversionState.origin_eid",
            inputType: "text",
            render: (_, row) =>
                row.conversionState?.origin_eid ? <ShortenedIdWithCopy id={row.conversionState.origin_eid} /> : "-",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.origin_eid",
            sorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            key: "conversionState.eid",
            dataIndex: "conversionState.eid",
            inputType: "text",
            render: (_, row) => (row.conversionState?.eid ? <ShortenedIdWithCopy id={row.conversionState.eid} /> : "-"),
            filterable: true,
            filterType: FILTER_TYPES.eid,
            sorter: false, //filter by eid don't allowed - doesn't make sense because of random string
            ellipsis: true,
            width: 150,
        },
        TableColumn.Entity.Owner<ConnectionSequenceStepLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            dataIndex: "owner.nickname",
            key: "owner.nickname",
            filterName: "owner_id",
            sortName: "owner.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (record) => record.owner,
        }),
        TableColumn.Entity.Advertiser<ConnectionSequenceStepLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
            ellipsis: true,
            width: 150,
            getEntity: (record) => record.advertiser,
        }),
        TableColumn.Entity.Offer<ConnectionSequenceStepLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            ellipsis: true,
            width: 150,
            getEntity: (record) => record.offer,
        }),
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "outgoingPostback.name",
            key: "outgoingPostback.name",
            inputType: "text",
            render: (_, row) => <EntityLinkCell.OutgoingPostback entity={row.outgoingPostback} />,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "outgoingPostback.name",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("request")} />,
            dataIndex: "request_name",
            key: "request_name",
            inputType: "text",
            render: (_, row) => row.request_name ?? "-",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "request_name",
            sorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("parent_id")} />,
            dataIndex: "conversionState.parent_id",
            key: "conversionState.parent_id",
            inputType: "text",
            render: (_, row) =>
                row.conversionState?.parent_id ? <ShortenedIdWithCopy id={row.conversionState.parent_id} /> : "-",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.parent_id",
            sorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("external_id")} />,
            dataIndex: "conversionState.advertiser_custom_id",
            key: "conversionState.advertiser_custom_id",
            inputType: "text",
            render: (_, row) =>
                row.conversionState?.advertiser_custom_id ? (
                    <ShortenedIdWithCopy id={row.conversionState.advertiser_custom_id} />
                ) : (
                    "-"
                ),
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.advertiser_custom_id",
            sorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("type")} />,
            dataIndex: "conversionState.type",
            key: "conversionState.type",
            inputType: "text",
            render: (_, row) => row.conversionState?.type ?? "-",
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "conversionState.type",
            filterOptions: [
                { label: tGL("lead"), value: "lead" },
                { label: tGL("click"), value: "click" },
                { label: tGL("send"), value: "send" },
            ],
            sorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("result")} />,
            dataIndex: "result",
            key: "result",
            inputType: "text",
            render: (_, row) => {
                const status = row.result;
                let color;
                switch (status) {
                    case ConnectionSequenceStepLogResult.Approved:
                        color = "green";
                        break;
                    case ConnectionSequenceStepLogResult.Error:
                        color = "red";
                        break;
                    case ConnectionSequenceStepLogResult.Pending:
                        color = "blue";
                        break;
                    case ConnectionSequenceStepLogResult.Unprocessed:
                        color = "red";
                        break;
                    case ConnectionSequenceStepLogResult.Rejected:
                        color = "red";
                        break;
                }
                return <Tag color={color}>{status ?? "-"}</Tag>;
            },
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "result",
            sorter: true,
            filterOptions: (
                Object.keys(ConnectionSequenceStepLogResult) as Array<keyof typeof ConnectionSequenceStepLogResult>
            ).map((key) => ({
                label: key,
                value: ConnectionSequenceStepLogResult[key],
            })),
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "is_success",
            key: "is_success",
            render: (is_success) => {
                return is_success ? (
                    <Tag icon={<CheckCircleOutlined />} color="green">
                        {tGL("ok")}
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="red">
                        {tGL("fail")}
                    </Tag>
                );
            },
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "is_success",
            filterOptions: [
                { label: tGL("ok"), value: true },
                { label: tGL("fail"), value: false },
            ],
            sorter: true,
            ellipsis: true,
            width: 100,
        },
    ];

    return (
        <>
            <Table<GetConnectionSequenceStepLogsResponse, ConnectionSequenceStepLogData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetConnectionSequenceStepLogsResponse}
                dataSource={tableFetchDataUtils.tableData?.connectionSequenceStepLogs || []}
                columnConfig={columnConfig}
                loading={getConnectionSequenceStepLogsQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterMode={"minute"}
                hideActionsColumn={true}
                dateFilterDataIndex={"created_at"}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                autoScrollWidth={true}
            />
            <LogConnectionDrawerDetail type={ConnectionSequenceStepLogModelType.OutgoingPostback} />
        </>
    );
};
